import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdditionalIcon from '@/assets/smartcity/additional-btn.svg';
import AvayaIcon from '@/assets/smartcity/avaya-btn.svg';
import AvayaDark from '@/assets/smartcity/avaya-dark.svg';
import Timer from '@/assets/smartcity/icon-time.svg';
import WaIcon from '@/assets/smartcity/wa-btn.svg';
import {
  type AmbulanceTypes,
  type EventTypes,
  ListEventTypes,
  StatusAmbulance,
} from '@/domains/traffic-monitoring/constants/listEvent.constant';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';

type CardType = 'header' | 'default';

const AVAYA_EXTENSION_IDS = new Set(['EXT_ID_TP_003', '003']);
const ADDITIONAL_EXTENSION_IDS = new Set(['EXT_ID_TP_004', '004', 'EXT_ID_TP_005', '005']);

interface AmbulanceItem {
  platNumber: string;
  ambulanceTypes: AmbulanceTypes;
  statusLabel: string;
  // Fleet/driver status is known and isn't AMB_ST_OFF, matched by plate
  // number against the ambulance roster by the caller - distinct from
  // ambulanceTypes above, which is this report's own per-ambulance status
  // (AMB_RPT_ST_*).
  isNotOffline?: boolean;
}

interface CardEventPscProps {
  typeCard?: CardType;
  eventTypes: EventTypes;
  eventTypeLabel: string;
  styleCard?: string;
  action?: string;
  report_code_id?: string | number;
  address: string;
  timer: number;
  textButton: string;
  totalNotif: number;
  // Intake channel (cm_code EXT_ID_TP_*) controls the card's channel icon.
  // Optional so legacy and unknown values keep the WhatsApp default.
  extensionId?: string | null;
  // A healthcare account never sees Avaya/WhatsApp channel data - the backend
  // already omits extension_id for them, but the caller passes this
  // explicitly instead of relying on absent data alone.
  showChannelIcon?: boolean;

  showButton?: boolean;
  showAmbulance?: boolean;

  ambulances?: AmbulanceItem[];

  onClick?: () => void;
  onWhatsApp?: () => void;
  // Fires on a click anywhere on the card body (not the action button, which
  // keeps its own onClick) - used to zoom the map to this report's location.
  onCardClick?: () => void;
}

const CardEventPsc = ({
  typeCard = 'default',
  eventTypes,
  eventTypeLabel,
  styleCard,
  action,
  address,
  timer,
  textButton,
  showButton = true,
  showAmbulance = true,
  ambulances = [],
  onWhatsApp,
  report_code_id,
  onClick,
  onCardClick,
  totalNotif,
  extensionId = null,
  showChannelIcon = true,
}: CardEventPscProps) => {
  const { t } = useTranslation();
  const { NEWCOLOR } = useColorStore();

  const getEventType = ListEventTypes[eventTypes];
  const isAvaya = extensionId != null && AVAYA_EXTENSION_IDS.has(extensionId);
  const isAdditional = extensionId != null && ADDITIONAL_EXTENSION_IDS.has(extensionId);
  const isDark = useThemeStore((state) => state.isDark);
  const channelIcon = isAdditional
    ? AdditionalIcon
    : isAvaya && isDark
      ? AvayaDark
      : isAvaya
        ? AvayaIcon
        : WaIcon;
  const channelName = isAdditional ? 'additional' : isAvaya ? 'avaya' : 'whatsapp';

  return (
    <div className="w-full">
      {/* {typeCard === 'header' && (
        <div
          className="w-full flex justify-between gap-3 px-2 p-1 rounded-t-[12px] italic title3 capitalize"
          style={{ backgroundColor: NEWCOLOR[getEventType.color] }}
        >
          <span className="text-text-white">Priority</span>
          <span>{action}</span>
        </div>
      )} */}

      {/* biome-ignore lint/a11y/useSemanticElements: this card wraps its own <button>s (WhatsApp icon, action button) - a native <button> can't contain them per HTML5, so role="button" + onKeyDown covers keyboard access instead */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCardClick}
        onKeyDown={(e) => {
          if (onCardClick && (e.key === 'Enter' || e.key === ' ')) onCardClick();
        }}
        className={cn(
          'w-full flex flex-col gap-3 p-3 border bg-db-border bg-db-card rounded-[12px]',
          // typeCard === 'header' ? 'rounded-b-[12px]' : '',
          onCardClick ? 'cursor-pointer' : '',
          styleCard
        )}
        // style={{ backgroundColor: getEventType.bg }}
      >
        <div className="w-full flex items-center gap-3 justify-between">
          <span
            className="w-fit p-2 rounded-full title2 uppercase text-white"
            style={{ backgroundColor: NEWCOLOR[getEventType.color] }}
          >
            {eventTypeLabel}
          </span>

          {showChannelIcon && (
            <button
              type="button"
              className="relative cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onWhatsApp?.();
              }}
            >
              <img src={channelIcon} alt={channelName} />
              {totalNotif > 0 && (
                <div className="w-5 h-5 rounded-full absolute top-[-5px] right-[-4px] bg-red-500 items-center flex justify-center">
                  <span className="caption text-text-white">{totalNotif}</span>
                </div>
              )}
            </button>
          )}
        </div>

        <span className={cn('title1bold  line-clamp-1')}>{report_code_id}</span>
        <span className={cn('title2  text-text-grey')}>{address}</span>

        <div className="flex justify-between items-center">
          <div className="inline-flex gap-1">
            <img src={Timer} alt="timer" className={cn('w-fit dark:invert')} />
            <span className={cn('')}>{t('psc>report>duration')}</span>
          </div>

          <div className="inline-flex gap-1 title2">
            <span className={cn('title2med')}>{timer}</span>
            <span className={cn('title2med')}>{t('psc>report>minutes')}</span>
          </div>
        </div>

        {showAmbulance && ambulances.length > 0 && (
          <div className="flex flex-col gap-3">
            {ambulances.map((ambulance, index) => {
              const status = StatusAmbulance[ambulance.ambulanceTypes];

              return (
                <div
                  key={index}
                  className="w-full uppercase py-1 px-3 rounded-[4px] bg-[#F3F6FE]  flex justify-between"
                >
                  {/* <span className="text-black body1">{ambulance.platNumber}</span> */}
                  <span
                    className={cn(
                      'body1',
                      ambulance.isNotOffline ? 'text-[#15803d]' : 'text-black'
                    )}
                  >
                    {ambulance.platNumber}
                  </span>

                  <div className="inline-flex gap-1 items-center">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: NEWCOLOR[status.color],
                      }}
                    />

                    <span
                      className="body1"
                      style={{
                        color: NEWCOLOR[status.color],
                      }}
                    >
                      {ambulance.statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showButton && (
          <button
            type="button"
            // customButton="hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            // label={textButton}
            className={`
            rounded-full
            hover:opacity-80
            cursor-pointer
            capitalize
            border
            px-4
            py-2
            title1medium
            text-white
          `}
            style={{
              backgroundColor: NEWCOLOR.bluePrimary,
            }}
          >
            {textButton}
          </button>
        )}
      </div>
    </div>
  );
};

export default CardEventPsc;
